import React from 'react';
import { Box, RotateCw, Ruler, Layers, Eye, EyeOff } from 'lucide-react';
import { useUiStore } from '../stores/useUiStore';

export const EnvelopeEditor: React.FC = () => {
  const envWidth = useUiStore((s) => s.envWidth);
  const envLength = useUiStore((s) => s.envLength);
  const envHeight = useUiStore((s) => s.envHeight);
  const envRotation = useUiStore((s) => s.envRotation);
  const envSetback = useUiStore((s) => s.envSetback);
  const showEnvelope = useUiStore((s) => s.showEnvelope);
  const showMedoids = useUiStore((s) => s.showMedoids);
  const showLasers = useUiStore((s) => s.showLasers);
  const showDrainage = useUiStore((s) => s.showDrainage);
  const showSetback = useUiStore((s) => s.showSetback);

  const setEnvWidth = useUiStore((s) => s.setEnvWidth);
  const setEnvLength = useUiStore((s) => s.setEnvLength);
  const setEnvHeight = useUiStore((s) => s.setEnvHeight);
  const setEnvRotation = useUiStore((s) => s.setEnvRotation);
  const setEnvSetback = useUiStore((s) => s.setEnvSetback);
  const setShowEnvelope = useUiStore((s) => s.setShowEnvelope);
  const setShowMedoids = useUiStore((s) => s.setShowMedoids);
  const setShowLasers = useUiStore((s) => s.setShowLasers);
  const setShowDrainage = useUiStore((s) => s.setShowDrainage);
  const setShowSetback = useUiStore((s) => s.setShowSetback);

  const sliders = [
    { label: 'Width', value: envWidth, min: 0.5, max: 10, step: 0.1, unit: 'm', icon: <Ruler className="h-3.5 w-3.5" />, setter: setEnvWidth },
    { label: 'Length', value: envLength, min: 0.5, max: 10, step: 0.1, unit: 'm', icon: <Ruler className="h-3.5 w-3.5" />, setter: setEnvLength },
    { label: 'Height', value: envHeight, min: 0.5, max: 5, step: 0.1, unit: 'm', icon: <Box className="h-3.5 w-3.5" />, setter: setEnvHeight },
    { label: 'Rotation', value: envRotation, min: 0, max: 360, step: 5, unit: '°', icon: <RotateCw className="h-3.5 w-3.5" />, setter: setEnvRotation },
    { label: 'Setback', value: envSetback, min: 0, max: 5, step: 0.1, unit: 'm', icon: <Layers className="h-3.5 w-3.5" />, setter: setEnvSetback },
  ];

  const toggles = [
    { label: 'Envelope', show: showEnvelope, setter: setShowEnvelope },
    { label: 'Medoids', show: showMedoids, setter: setShowMedoids },
    { label: 'Lasers', show: showLasers, setter: setShowLasers },
    { label: 'Drainage', show: showDrainage, setter: setShowDrainage },
    { label: 'Setback', show: showSetback, setter: setShowSetback },
  ];

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        Building Envelope
      </h2>

      {/* Toggles */}
      <div className="flex flex-wrap gap-1.5">
        {toggles.map((t) => (
          <button
            key={t.label}
            onClick={() => t.setter(!t.show)}
            aria-pressed={t.show}
            className={[
              'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
              t.show
                ? 'bg-sky-900/40 text-sky-300 border border-sky-800/50'
                : 'bg-slate-800/50 text-slate-500 border border-slate-700/30 hover:text-slate-300',
            ].join(' ')}
          >
            {t.show ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Sliders */}
      <div className="flex flex-col gap-2">
        {sliders.map((s) => (
          <div key={s.label} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-slate-400">
                {s.icon}
                {s.label}
              </span>
              <span className="font-mono text-sky-300">
                {s.value.toFixed(s.step < 1 ? 1 : 0)}{s.unit}
              </span>
            </div>
            <input
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={s.value}
              onChange={(e) => s.setter(parseFloat(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-700 accent-sky-400"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
